import {BaseElementAsync, MsgLabel, OSkinableInit, PickInit} from "/@back@/commons/basis.js";
import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {IView} from "/@lib@/commons/views.js";
import {DOM, JSX, ShadowJsx} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IWspUiEnv, Wsp} from "/@lib@/wsp/wsp.js";
import {ESearchExportColumnStatus, IExportColsPersist, ISearchFunc, ISearchStore, SearchExportColumn, SearchRequest} from "/@lib@/wsp/search.js";
import {ActionBtn, Button} from "/@back@/commons/widgets/buttons.js";
import {AppHeader, OAppHeaderInit} from "/@back@/core/widgets/appHeader.js";
import {JSrcFields, srcUri} from "/@lib@/wsp/src.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {ACTION, Action, ActionMenu, IAction} from "/@lib@/commons/actions.js";
import {IO} from "/@lib@/commons/io/io.js";
import "/@back@/wsp/dialogs/dialogs_Perms.js";
import {InputOrderedSet, IOrderedSetEntry, OOrderedSetInit} from "/@back@/commons/widgets/inputs.js";

export interface OSearchExportCsvInit extends OSkinableInit,
	PickInit<SearchExportCsv, 'columns' | 'buildWhereXmlFct' | 'privateUri' | 'bibConfsLists' | 'appHeaderMainActionsLists'> {
	reg?: IReg<IWspUiEnv>

	/** Init custom de l'input de choix des colonnes */
	inputEltInit?: OOrderedSetInit
}

export interface SearchExportCsv extends BaseElementAsync, IView, ISearchStore {
	initialize(init: OSearchExportCsvInit): this
}

/**
 * Dialogue d'export de résultat de recherche sous forme de CSV
 *
 * <br> Déclaration de 'configurations de colonnes' dans la blibliothèque via des objets
 *  de type {@link IExportColsPersist} sur la liste `confcsv:searchExportCsv:bib`
 * Exemple :
 * <pre>
 * REG.reg.addToList("confcsv:searchExportCsv:bib", "test1", 1, {
 *		title : "Liste prédéfinie 1",
 *		columns: ['dt', 'srcCode']
 *	} as IExportColsPersist);
 * </pre>
 *
 */
export class SearchExportCsv extends BaseElementAsync implements IView, ISearchStore {

	reg: IReg<IWspUiEnv>;

	/** Colonnes disponibles */
	columns: SearchExportColumn<IRegPointer<IWspUiEnv>>[];

	/** fonction retournant le résultat de recherche */
	buildWhereXmlFct: (ctx: IRegPointer<IWspUiEnv>) => Element;

	/** srcUri du privateFolder dans lequel enregistrer les listes de cols */
	privateUri: srcUri = '/itemSearchesColsCsv.json';

	/** Listes des structures de données {@see IExportColsPersist} définissant des configs de colonnes proposées dans la bibliothèque */
	bibConfsLists: string[] = ["confcsv:searchExportCsv:bib"];

	/** Init de la liste d'actions du appHeader */
	appHeaderMainActionsLists?: string[] = [SearchExportCsv.APPBAR_MAIN_ACTIONS];

	get wsp(): Wsp {return this.reg.env.wsp};

	/** Eléments UI */
	protected _listColumnsInpt: InputOrderedSet;

	protected _exportBtn: Button;

	protected _headerElt: AppHeader<this>;

	protected _titleElt: MsgLabel;

	protected _csvBiblioMenuAction: CsvBiblioActionMenu<SearchExportCsv>;

	public static APPBAR_MAIN_ACTIONS = "actions:searchExportCsv:bar";

	private static LIAISE_MSG_SAVED = "searches.csv.saved";

	protected async _initialize(init: OSearchExportCsvInit): Promise<void> {
		// Reg dédié car enrichi d'actions contextuelles
		this.reg = this.reg = REG.createSubReg(this.findReg(init));
		this.columns = init.columns;
		this.buildWhereXmlFct = init.buildWhereXmlFct;
		if (init.privateUri) this.privateUri = init.privateUri;
		if (init.bibConfsLists) this.bibConfsLists = init.bibConfsLists;

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("webzone:panel", sr);
		this.reg.installSkin("standard-dialog", sr);
		this._initAndInstallSkin(this.localName, init);

		/* Exemple : Déclaration de 'ExportCsvCols' prédéfinies dans la bibliothèque */
		/*
		REG.reg.addToList("confcsv:searchExportCsv:bib:item", "test1", 1, {
			title: "Liste prédéfinie 1",
			columns: ['dt', 'srcCode']
		} as IExportColsPersist);
		REG.reg.addToList("confcsv:searchExportCsv:bib:item", "test2", 1, {
			title: "Liste prédéfinie 2",
			columns: ['space']
		} as IExportColsPersist);
		REG.reg.addToList("confcsv:searchExportCsv:bib:task", "testTask1", 1, {
			title: "Liste prédéfinie de task 1",
			columns: ['space']
		} as IExportColsPersist);
		 */

		// Init REG
		const CsvBiblioActionMenu_actionLists = "actions:searchExportCsv:bib:" + this.id;
		this._csvBiblioMenuAction = new CsvBiblioActionMenu().setActionLists(CsvBiblioActionMenu_actionLists);
		this.reg.addToList(SearchExportCsv.APPBAR_MAIN_ACTIONS, "csvBiblioMenuAction", 1, this._csvBiblioMenuAction, 10);
		this.reg.addToList(CsvBiblioActionMenu_actionLists, 'updateCsvConf', 1, UpdateCsvConf.SINGLETON, 10);
		this.reg.addToList(CsvBiblioActionMenu_actionLists, 'saveCsvConf', 1, SaveCsvConfAs.SINGLETON, 20);

		// Construction de la liste des colonnes proposées
		let dataSet: Dict<IOrderedSetEntry> = {};
		this.columns.map((col) => {
			if (col.isAvailable(this)) {
				dataSet[col.getId()] = {
					label: col.getLabel(this),
					description: col.getDescription(this),
					icon: col.getIcon(this),
					notAvailable: col.getExportStatus(this) === ESearchExportColumnStatus.notAvailable,
				};
			}
		});

		this._headerElt = sr.appendChild(new AppHeader().initialize({
			reg: this.reg,
			actionContext: this,
			mainActions: this.reg.getList(SearchExportCsv.APPBAR_MAIN_ACTIONS),
			mainBar: {
				actions: this.reg.mergeLists(...init.appHeaderMainActionsLists),
				groupOrder: 'burger crits io *',
			},
		} as OAppHeaderInit<SearchExportCsv>)) as AppHeader<this>;

		this._titleElt = sr.appendChild(<MsgLabel î={{label: '', skinOver: 'wsp-search-export-csv/msgTitle'}}/>) as MsgLabel;

		const initInput: OOrderedSetInit = Object.assign({
			dataSet,
			selectionLabel: "Colonnes",
			availableLabel: "",
			emptySelectionMsg: "Aucune colonne",
			previewMode: "asLines",
		} as OOrderedSetInit, init.inputEltInit);
		this._listColumnsInpt = sr.appendChild(new InputOrderedSet().initialize(initInput));
		this._listColumnsInpt.addEventListener("change", (ev) => {
			this._refreshUi();
			if (!this.currentDisplayedColsWritable)
				this.razSpecificColsConf();
		});

		this._exportBtn = <Button label="Exporter..." class="default" ui-context="dialog" onclick={this.onExportBtn}/> as Button;
		sr.appendChild(
			<footer id="footer">
				{this._exportBtn}
				<Button label="Annuler" ui-context="dialog" onclick={this.onCancelBtn}/>
			</footer>
		);

		this.razForm();
		this.refresh();
	}

	/**
	 * Réinitialisation totale de l'env aux valeurs initiales
	 */
	razForm() {
		let selectedValues: string[] = [];
		this.columns.map((col) => {
			if (col.isAvailable(this))
				if (col.getExportStatus(this) === ESearchExportColumnStatus.exported) selectedValues.push(col.getId());
		});
		this._listColumnsInpt.value = selectedValues;
		this.razSpecificColsConf();
	}

	private razSpecificColsConf() {
		this._titleElt.setCustomMsg(null);
		this.currentDisplayedColsConf = null;
		this.currentDisplayedColsWritable = null;
	}

	public currentDisplayedColsConf: IExportColsPersist;
	public currentDisplayedColsWritable: boolean;

	/**
	 * Affiche une colsConf donnée (issue de la bibliothèque)
	 * @param conf
	 * @param title
	 * @param writable
	 */
	displayBibConf(conf: IExportColsPersist, title?: string, writable = false) {
		if (conf) {
			this.currentDisplayedColsConf = conf;
			this.currentDisplayedColsWritable = writable;
			this._listColumnsInpt.value = conf.columns;
			this._titleElt.setCustomMsg(title || conf.title, "valid");
			if (conf.description) this._titleElt.title = conf.description;
			else delete this._titleElt.title;
		} else {
			this.razForm();
		}
	}

	refresh() {
		super.refresh();
		CsvBiblioActionMenu.makeActionsList(this._csvBiblioMenuAction, this.bibConfsLists, this).then(() => {
			if (this._headerElt) this._headerElt.refresh();
			this._refreshUi();
		});
	}

	private async _refreshUi() {
		this._exportBtn.disabled = (!this._listColumnsInpt || !this._listColumnsInpt.value?.length) ? true : false;
	}

	/**
	 * Export du CSV
	 */
	async onExportBtn(this: Button): Promise<void> {
		const self = DOMSH.findHost<SearchExportCsv>(this);
		const vals: string[] = self._listColumnsInpt.value;
		if (vals && vals.length) {
			const wsp = self.reg.env.wsp;
			const name = IO.getValidFileName(wsp.wspTitle, ".csv", 'date');
			const selectedColumnsSorted: { key: string, colDef: null | string | ISearchFunc }[] = Array.from(vals) as any;
			await Promise.all(vals.map(async (entry, pos) => {
				const colDef = self.columns.find(col => col.getId() === entry);
				selectedColumnsSorted[pos] = await colDef?.fillSearchColumn(self);
			}));
			const where = self.buildWhereXmlFct(self);
			try {
				if (where) {
					const url = self.reg.env.universe.config.wsps.searchUrl.resolve(IO.qs("cdaction", "Search", "param", wsp.code, "format", 'csv', "downloadFileName", name, "addColumnNames", true));
					const req = new SearchRequest(selectedColumnsSorted).setWhereXml(where)
					await IO.saveRespAs(url, self.reg.env.uiRoot, new Map().set("request", DOM.serializer().serializeToString(req.toDom())), "text/csv", "POST");
					POPUP.findPopupableParent(this).close();
				} else
					return ERROR.show("Requête invalide");
			} catch (e) {
				return ERROR.log(e);
			}
		}
	}

	onCancelBtn(this: Button) {
		POPUP.findPopupableParent(this).close();
	}

	/** Gestion des confs personnelles */
	private _udateLastSavedConfs(confs: Promise<JSearchesExportCsvDatas>, srcDt?: number) {
		srcDt = (confs && srcDt) ? srcDt : -1;
		if (srcDt == this._lastSavedConfs_srcDt) return;
		this._lastSavedConfs = confs;
		this._lastSavedConfs_srcDt = srcDt;
		this.refresh();
	}

	private _lastSavedConfs: Promise<JSearchesExportCsvDatas>;
	private _lastSavedConfs_srcDt = -1;

	/** Retourne la liste des confs personnelles (avec gestion de cache) */
	async savedSearchesCsv(): Promise<JSearchExportCsvDatas[] | null> {
		if (!this._lastSavedConfs) await this._savedSearchesCsvFetch();
		if (this._lastSavedConfs) {
			const conf = await this._lastSavedConfs;
			return conf.searchesCsv;
		}
	}

	private async _savedSearchesCsvFetch(): Promise<JSearchesExportCsvDatas | null> {
		try {
			if (!this.wsp.isAvailable) await this.wsp.waitForAvailable(this);
			const resp = await this.wsp.wspServer.config.privateFolderUrl.fetch(IO.qs("cdaction", "GetSrc", "format", "stream", "fields", "srcDt", "param", this.wsp.code, "refUri", "", "privateUri", this.privateUri), 'json');
			if (resp.status === 200) {
				this._udateLastSavedConfs(resp.asJson as Promise<JSearchesExportCsvDatas>, (JSON.parse(resp.headers.get("X-SCFIELDS")) as JSrcFields).srcDt);
			} else {
				this._udateLastSavedConfs(null);
			}
			return this._lastSavedConfs;
		} catch (e) {
			this._udateLastSavedConfs(null);
			ERROR.log("fetchSearches failed", e);
		}
	}

	/** Enregistrement */
	async saveCurrentBibConf(id: string, title: string, silent = false): Promise<true | false | null> {
		if (this._listColumnsInpt.value?.length) {
			return this._saveBibConf({
				id,
				title,
				columns: this._listColumnsInpt.value
			}, silent);
		} else
			return false;
	}

	async removeSavedBibConf(id: string) {
		if (this.currentDisplayedColsConf?.id === id)
			this.razForm();
		return this._saveBibConf({id} as IExportColsPersist, true, true);
	}

	async persistSearch(search: IExportColsPersist): Promise<any> {
		await this._saveBibConf(search, false);
	}

	/** Ajoute {@param search} à la liste des enregistrements
	 * @return true : l'enregistrement a bien eu lieu
	 * @return false : l'enregistrement n'a pas eu lieu de façon volontaire
	 * @return null : l'enregistrement n'a pas eu lieu de façon involontaire (erreur, ...)
	 */
	private async _saveBibConf(search: IExportColsPersist, silent = false, remove = false): Promise<true | false | null> {
		try {
			if (!this.wsp.isAvailable) await this.wsp.waitForAvailable(this);
			if (!search.id) throw "search must have ID to be saved";

			let savedSearch = {} as JSearchesExportCsvDatas;

			// - Récupération de la liste actuelle pour update
			savedSearch.searchesCsv = await this.savedSearchesCsv() || [];

			// - Ajout / maj de la liste à enregistrer

			if (remove) {
				savedSearch.searchesCsv = savedSearch.searchesCsv.filter(entry => entry.id === search.id ? false : true);
			} else {
				const findCurrentSearch = savedSearch.searchesCsv.find(entry => entry.id === search.id);
				const title = search.title;
				if (findCurrentSearch) {
					if (silent || await POPUP.confirm(`Voulez-vous vraiment remplacer l'entrée '${title}' existante ?`, null, {okLbl: "Remplacer", cancelLbl: "Annuler"})) {
						findCurrentSearch.title = search.title;
						findCurrentSearch.columns = search.columns;
						findCurrentSearch.description = search.description;
						findCurrentSearch.params = search.params;
					} else
						return false;
				} else
					savedSearch.searchesCsv.push(search as JSearchExportCsvDatas);
			}

			// - Enregistrement
			const fields = await this.wsp.wspServer.config.privateFolderUrl.fetchJson<JSrcFields>(IO.qs("cdaction", "PutSrc", "format", "JSON", "fields", "srcDt", "param", this.wsp.code, "refUri", "", "privateUri", this.privateUri), {
				method: "POST",
				body: JSON.stringify(savedSearch),
				headers: {"Content-Type": "appplication/json"}
			});
			this._udateLastSavedConfs(Promise.resolve(savedSearch), fields.srcDt);
			this.reg.env.universe.wsFrames.ws.postMsg({svc: 'liaise', type: SearchExportCsv.LIAISE_MSG_SAVED, uri: this.privateUri});
			return true;

		} catch (e) {
			ERROR.log(`Save searches csv to '${this.privateUri}' failed`, e);
			POPUP.showNotifError("L'enregistrement de la configuration des colonnes a échoué.", this);
		}
	}

	onViewShown() {
		if (!this._onLiaiseMsg) {
			this._onLiaiseMsg = this.onLiaiseMsg.bind(this);
			this.reg.env.universe.wsFrames.ws.msgListeners.on('liaise', this._onLiaiseMsg);
		}
		if (!this._onConnRenewed) {
			this._onConnRenewed = this.onConnRenewed.bind(this);
			this.reg.env.place.eventsMgr.on("onConnectionRenewed", this._onConnRenewed);
		}
	}

	onViewHidden(closed?: boolean) {
		if (closed) {
			this.reg.env.universe.wsFrames.ws.msgListeners.removeListener('liaise', this._onLiaiseMsg);
			this.reg.env.place.eventsMgr.removeListener("onConnectionRenewed", this._onConnRenewed);
		}
	}

	/** Détection d'une écriture concurente via le service de liaise. */
	protected _onLiaiseMsg: (m: Jsonisable) => void;

	protected onLiaiseMsg(m: Jsonisable) {
		if (m.type === SearchExportCsv.LIAISE_MSG_SAVED && m.uri === this.privateUri) this._udateLastSavedConfs(null);
	}

	protected _onConnRenewed: () => void;

	protected async onConnRenewed() {
		const resp = await this.wsp.wspServer.config.privateFolderUrl.fetch(IO.qs("cdaction", "GetSrc", "format", "JSON", "fields", "srcDt", "param", this.wsp.code, "refUri", "", "privateUri", this.privateUri), 'json');
		if (resp.status === 200 ? this._lastSavedConfs_srcDt !== resp.asJson.srcDt : resp.status === 404 && this._lastSavedConfs_srcDt !== -1) this._udateLastSavedConfs(null);
	}
}

REG.reg.registerSkin('wsp-search-export-csv', 1, /* language=CSS */ `
	c-appheader {
		border-bottom: 1px solid var(--border-color);
	}

	c-msg {
		flex: 0;
		display: contents;
		text-align: center;
	}

	c-msg[hidden] {
		display: none;
	}

	c-input-ordered-set {
		flex: 1;
		margin: 5px;
	}
`);

REG.reg.registerSkin('wsp-search-export-csv/msgTitle', 1, /* language=CSS */ `
	.label {
		color: var(--alt1-color);
	}
`);


customElements.define("wsp-search-export-csv", SearchExportCsv);


/** Schéma json d'enregistrement des confs de des cols */
interface JSearchesExportCsvDatas {
	searchesCsv: JSearchExportCsvDatas[]
}

interface JSearchExportCsvDatas extends IExportColsPersist {
	id: string
}


// Actions

/** Réinitialisation du formulaire dans son état initial lors de l'ouverture de la fenêtre */
class ResetForm extends Action<SearchExportCsv> {
	static SINGLETON = new ResetForm();

	constructor() {
		super("resetForm");
		this._label = "Réinitialiser le choix des colonnes";
		this._icon = "/@skin@/commons/icons/reset.svg";
		this._group = 'cols';
	}

	async execute(ctx: SearchExportCsv, ev?: Event) {
		return ctx.razForm();
	}

}

REG.reg.addToList(SearchExportCsv.APPBAR_MAIN_ACTIONS, "resetForm", 1, ResetForm.SINGLETON);

/** Enregistremet d'une conf */
class SaveCsvConfAs extends Action<SearchExportCsv> {
	static SINGLETON = new SaveCsvConfAs();

	constructor() {
		super("saveCsvConf");
		this._label = "Enregistrer la configuration sous...";
		this._description = "Enregistrer la configuration des colonnes courante";
		//this._icon = "/@skin@/core/dialogs/save.svg";
		this._group = 'io';
	}

	async execute(ctx: SearchExportCsv, ev?: Event) {
		let input = <input type="text" pattern=".+" id="idIpt"/>;

		async function showConfirm() {
			if (await POPUP.confirm(<div>
				<ShadowJsx skinOver="form-control-areas">
					<div class="ctrlLbl">
						<label class="lbl" control="idIpt">Libellé :</label>
						{input}
					</div>
				</ShadowJsx>
			</div>, null, {okLbl: "Enregistrer", cancelLbl: "Annuler", titleBar: {barLabel: {label: "Enregistrement de la configuration de l'export CSV"}}})) {
				if (input.value) {
					const result = await ctx.saveCurrentBibConf(input.value, input.value, false);
					if (result === false) await showConfirm();
				}
			}
		}

		await showConfirm();
	}

}

/** Update d'une conf */
class UpdateCsvConf extends Action<SearchExportCsv> {
	static SINGLETON = new UpdateCsvConf();

	constructor() {
		super("updateCsvConf");
		this._label = (ctx) => {
			const label = ctx.currentDisplayedColsConf?.title || ctx.currentDisplayedColsConf?.id;
			return `Remplacer la configuration '${label}'`;
		};
		this._description = "Actualiser la configuration actuelle";
		//this._icon = "/@skin@/core/dialogs/save.svg";
		this._group = 'io';
	}

	isVisible(ctx: SearchExportCsv): boolean {
		if (!ctx.currentDisplayedColsConf || !ctx.currentDisplayedColsWritable) return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: SearchExportCsv, ev?: Event) {
		const result = await ctx.saveCurrentBibConf(ctx.currentDisplayedColsConf.id, ctx.currentDisplayedColsConf.title, true);
	}

}

/**
 *
 */
class CsvBiblioActionMenu<C extends SearchExportCsv> extends ActionMenu<C> {

	/** (re)Construit la liste des actions
	 * ATTENTION : nécessite un refresh de la barre d'action container
	 */
	static async makeActionsList(csvBiblioAction: CsvBiblioActionMenu<SearchExportCsv>, listsActsTpls: string[], ctx: SearchExportCsv): Promise<void> {

		let acts: IAction<SearchExportCsv>[];
		// - actions issues de la listes d'actions
		const actionLists = (typeof csvBiblioAction._actionLists === 'function') ? csvBiblioAction._actionLists(ctx, csvBiblioAction) : csvBiblioAction._actionLists;
		acts = actionLists ? REG.getReg(ctx).mergeLists<Action<SearchExportCsv>>(...actionLists.split(' ')) : [];

		// - actions 'tpl'
		if (listsActsTpls) {
			const elts = ctx.reg.mergeLists(...listsActsTpls) as IExportColsPersist[];
			elts.forEach(entry => acts.push(new CsvBiblioEntryAction_tpl(entry)));
		}

		// - actions personnelles enregistrées
		const savedConfs = await ctx.savedSearchesCsv();
		if (savedConfs) {
			savedConfs.forEach(entry => acts.push(new CsvBiblioEntryAction_saved(entry)));
		}
		csvBiblioAction.setActions(ACTION.injectSepByGroup(acts, '* act-saved act-tpls', ctx));
	}

	constructor(id?: string) {
		super(id);
		this._label = "Bibliothèque";
		this._group = "lists";
		this._icon = "/@skin@/wsp/dialogs/searchExportCsv/bibCols.svg";
	}

}


class CsvBiblioEntryAction_tpl<C extends SearchExportCsv> extends Action<C> {
	private _searchId: string;

	constructor(protected colSetDef: IExportColsPersist) {
		super(colSetDef.id);
		this._label = colSetDef.title;
		this._description = colSetDef.description;
		this._group = "act-tpls";
	}

	execute(ctx: C, ev?: Event): any | 'noPreventDefault' {
		const title = this.colSetDef.title;
		ctx.displayBibConf(this.colSetDef, title ? `Bibliothèque : ${title}` : null, false);
	}
}

class CsvBiblioEntryAction_saved<C extends SearchExportCsv> extends Action<C> {
	private _searchId: string;

	constructor(protected colSetDef: IExportColsPersist) {
		super(colSetDef.id);
		this._label = colSetDef.title;
		this._description = colSetDef.description;
		this._skinOver = "search-export-csv/saved/entry";
		this._group = "act-saved";
	}

	execute(ctx: C, ev?: Event): any | 'noPreventDefault' {
		const title = this.colSetDef.title;
		ctx.displayBibConf(this.colSetDef, title ? `Personnelle : ${title}` : null, true);
	}

	initButtonNode(buttonNode: Element, ctx: C) {
		super.initButtonNode(buttonNode, ctx);
		const title = this.colSetDef.title;
		buttonNode.appendChild(ActionBtn.buildButton(new Action()
			.setIcon("/@skin@/commons/icons/delete.svg")
			.setLabel(`Supprimer la configuration : ${title}`)
			.setExecute((ctx: C, ev) => {
				ctx.removeSavedBibConf(this.getId());
				DOM.setHidden(buttonNode, true);
				if (ev) {
					ev.stopImmediatePropagation();
					ev.preventDefault();
				}
			}), ctx, 'bar'));
	}
}

REG.reg.registerSkin("search-export-csv/saved/entry", 1, /* language=CSS */ `
	slot {
		position: absolute;
		right: 0;
		display: block;
	}


	/*:host{
		display: flex;
		flex-direction: row-reverse;
	}

  span{
		flex:1;
	}
	
	slot {
		border : 5px solid yellow;
	}*/

`);
